import * as Sentry from "@sentry/browser";
import React from "react";
import {
  ComponentType,
  lazy,
  LazyExoticComponent,
  useEffect,
  useState,
} from "react";

// The first-miss reload usually recovers silently, so it leaves no Sentry event
// on its own; sample a fraction to size how often a chunk miss forces a refresh.
// The genuine-failure rate is the share of these that come back as a second-miss
// crash in the chunk-load group, so a sample is enough to track the trend.
const RELOAD_CAPTURE_SAMPLE_RATE = 0.1;

// @ts-ignore
export const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    if (typeof window === "undefined" || !window.localStorage) {
      return componentImport();
    }

    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem("page-has-been-force-refreshed") || "false"
    );

    try {
      const component = await componentImport();

      window.localStorage.setItem("page-has-been-force-refreshed", "false");

      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        window.localStorage.setItem("page-has-been-force-refreshed", "true");

        if (Math.random() < RELOAD_CAPTURE_SAMPLE_RATE) {
          Sentry.captureMessage("Chunk load failed; forcing reload", {
            level: "warning",
            fingerprint: ["chunk-load-reload"],
            tags: { chunk_load_reload: true, online: navigator.onLine },
          });
          // The reload cancels in-flight requests, so let the envelope leave
          // first; flush resolves as soon as it's sent (or after the timeout).
          await Sentry.flush(2000);
        }

        window.location.reload();
        // Returning reload()'s undefined would make React.lazy read `.default`
        // off undefined in the next microtask before navigation unloads the
        // page — an unhandled rejection on essentially every chunk-miss reload.
        // A never-settling promise keeps the component suspended (PageLoader up)
        // until the reload tears the page down.
        return new Promise<never>(() => {});
      }

      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      // Let's let the application crash and raise the error.
      throw error;
    }
  });

// Resolve a missing optional module to a no-op component instead of rejecting.
// A per-partner file (e.g. _analytics.tsx) is optional once its data lives in
// _partner_config.tsx; Vite rejects the dynamic import for a slug with no file,
// which would otherwise force every partner to keep an empty stub. Used with
// lazyWithRetry, this also stops a missing file from tripping the force-refresh.
export const importOptional = (
  modulePromise: Promise<{ default: ComponentType<any> }>
): Promise<{ default: ComponentType<any> }> =>
  modulePromise.catch(() => ({ default: () => null }));

export function useLazyWithoutRetry(importFunc: () => void) {
  const [component, setComponent] =
    useState<LazyExoticComponent<ComponentType<any>>>();

  useEffect(() => {
    const component = lazyWithRetry(importFunc);
    setComponent(component);
    // only lazy once after the component is rendered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (component) {
    return component;
  }
  return () => <></>;
}
